import {createContext, useContext, useRef} from 'react';

import type {MutableRefObject, ReactNode} from 'react';

interface CaptchaCallbacks {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

interface BotCaptchaContextValue {
  pendingRef: MutableRefObject<CaptchaCallbacks | null>;
  waitForCaptchaResolution: () => Promise<string>;
}

const BotCaptchaContext = createContext<BotCaptchaContextValue | null>(null);

export const BotCaptchaContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const pendingRef = useRef<CaptchaCallbacks | null>(null);

  const waitForCaptchaResolution = (): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      // Reject any in-flight promise before overwriting — prevents orphaned
      // Promises when two concurrent 403s both trigger the captcha flow.
      pendingRef.current?.reject(new Error('superseded'));
      pendingRef.current = {resolve, reject};
    });

  return (
    <BotCaptchaContext.Provider value={{pendingRef, waitForCaptchaResolution}}>
      {children}
    </BotCaptchaContext.Provider>
  );
};

export const useBotCaptchaContext = (): BotCaptchaContextValue => {
  const ctx = useContext(BotCaptchaContext);
  if (!ctx)
    throw new Error(
      'useBotCaptchaContext must be used within BotCaptchaContextProvider'
    );
  return ctx;
};
